import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Search, FileText, Handshake, Key, Shield, TrendingUp } from 'lucide-react';
import { useSiteImage } from '@/contexts/SiteImagesContext';

const RealEstatePage = () => {
  const heroImage = useSiteImage('hero.real-estate', 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80');
  const buyingServices = [
    'Property search based on your criteria',
    'Due diligence and legal verification',
    'Price negotiation on your behalf',
    'Contract preparation and review',
    'Closing coordination',
    'Post-purchase support',
  ];

  const sellingServices = [
    'Free property valuation',
    'Professional photography and marketing',
    'Listing on multiple platforms',
    'Buyer screening and showings',
    'Offer negotiation',
    'Transaction management',
  ];

  const process = [
    {
      icon: Search,
      title: 'Discovery',
      description: 'We learn about your goals, budget, and preferences to find the perfect match.',
    },
    {
      icon: FileText,
      title: 'Due Diligence',
      description: 'Thorough legal checks, land certification verification, and market analysis.',
    },
    {
      icon: Handshake,
      title: 'Negotiation',
      description: 'We negotiate the best terms on your behalf with full transparency.',
    },
    {
      icon: Key,
      title: 'Closing',
      description: 'Seamless transaction management through to key handover.',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="real-estate-page">
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-warm-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-charcoal mb-6">
                Bali Real Estate<br />
                <span className="italic text-ubud-jungle">Made Simple</span>
              </h1>
              <p className="font-body text-lg text-charcoal-light mb-8">
                Buying or selling property in Bali can be complex. Our local expertise and professional approach make the process smooth and secure.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/properties?status=for_sale"
                  className="inline-flex items-center gap-2 bg-ubud-jungle text-white hover:bg-ubud-jungle-dark transition-all duration-300 rounded-full px-8 py-3.5 font-body font-medium"
                >
                  Browse Properties
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 border border-ubud-jungle text-ubud-jungle hover:bg-ubud-jungle/5 transition-all duration-300 rounded-full px-8 py-3.5 font-body font-medium"
                >
                  Free Valuation
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <img
                src={heroImage}
                alt="Bali Villa"
                className="rounded-3xl shadow-floating"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Split */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Buying */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-warm-sand p-8 lg:p-10 rounded-3xl"
            >
              <div className="w-14 h-14 bg-ubud-jungle/10 rounded-2xl flex items-center justify-center mb-6">
                <Key className="w-7 h-7 text-ubud-jungle" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-charcoal mb-4">
                Buying Property
              </h2>
              <p className="font-body text-charcoal-light mb-6">
                Whether you're looking for an investment property, a holiday home, or relocating to Bali, we'll help you find the perfect property.
              </p>
              <ul className="space-y-3 mb-8">
                {buyingServices.map((service) => (
                  <li key={service} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-ubud-jungle flex-shrink-0" />
                    <span className="font-body text-charcoal text-sm">{service}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/properties?status=for_sale"
                className="inline-flex items-center gap-2 text-ubud-jungle font-medium hover:gap-3 transition-all"
              >
                View Properties for Sale <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            {/* Selling */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-charcoal p-8 lg:p-10 rounded-3xl text-white"
            >
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Selling Property
              </h2>
              <p className="font-body text-white/70 mb-6">
                Looking to sell your Bali property? Our marketing expertise and buyer network ensure you get the best price.
              </p>
              <ul className="space-y-3 mb-8">
                {sellingServices.map((service) => (
                  <li key={service} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-ubud-jungle flex-shrink-0" />
                    <span className="font-body text-white/90 text-sm">{service}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-white font-medium hover:gap-3 transition-all"
              >
                Get Free Valuation <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-28 bg-warm-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-4">
              Our Process
            </h2>
            <p className="font-body text-lg text-charcoal-light max-w-2xl mx-auto">
              A transparent, step-by-step approach to buying or selling property in Bali.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {process.map((step, idx) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-card text-center"
              >
                <div className="w-14 h-14 bg-ubud-jungle/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-7 h-7 text-ubud-jungle" />
                </div>
                <h3 className="font-heading text-lg font-medium text-charcoal mb-2">
                  {step.title}
                </h3>
                <p className="font-body text-charcoal-light text-sm">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=800&q=80"
                alt="Bali Property"
                className="rounded-3xl shadow-floating"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-6">
                Why Work With Us?
              </h2>
              <div className="space-y-6">
                {[
                  {
                    icon: Shield,
                    title: 'Legal Protection',
                    desc: 'We verify all land certificates and ownership documents to protect your investment.',
                  },
                  {
                    icon: Search,
                    title: 'Local Knowledge',
                    desc: 'Deep understanding of each area\'s regulations, development plans, and market trends.',
                  },
                  {
                    icon: Handshake,
                    title: 'Trusted Network',
                    desc: 'Access to off-market opportunities through our extensive network of owners and developers.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-12 h-12 bg-warm-sand rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-ubud-jungle" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-medium text-charcoal mb-1">
                        {item.title}
                      </h3>
                      <p className="font-body text-charcoal-light text-sm">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-ubud-jungle">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6">
              Start Your Bali Property Journey
            </h2>
            <p className="font-body text-white/80 text-lg mb-8">
              Whether buying or selling, we're here to guide you every step of the way.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-ubud-jungle hover:bg-warm-sand transition-all duration-300 rounded-full px-8 py-4 font-body font-medium"
            >
              Schedule a Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RealEstatePage;
